/* istanbul ignore file */
import { useContext, useState, useEffect, RefObject } from "react";
import { AppContext } from "../app/store";
import {
  Plan,
  SearchResultPlan,
  DrugCostInfo,
  LowIncomeSubsidyStatus,
  PrescriptionFrequency,
  Pharmacy,
  DrugCostsResponse,
  DrugCostPrescriptionInfo,
  PrescriptionDrug,
  DrugInfo,
  IdParts,
} from "../@types";
import { getDrugCosts, getOtherDrugInfo } from "../api";
import { useTranslate } from "./intlHooks";
import { logError, useCurrentPlanYear } from ".";
import { getFullPlanId, makePlanUrlPartForApi } from "./objectUtilities";
import { isSearchResultPlan } from "../@types/guards";
import { ApiError } from "./errors";

export const getDefaultDrugCostInfo = ({
  plan,
  t,
}: {
  plan: Plan | SearchResultPlan;
  t: (id: string) => string;
}): DrugCostInfo => ({
  costs: [],
  lowest_mail_total: t("plan_card.calculating"),
  lowest_retail_total: t("plan_card.calculating"),
  plan: getFullPlanId(plan),
  excluded_drugs: [],
  restrictions: [],
});

export const getPrescriptionInfoForDrugCosts = (
  prescriptions: PrescriptionDrug[]
): DrugCostPrescriptionInfo[] =>
  prescriptions.map(p => ({
    ndc: p.ndc,
    frequency: p.frequency as PrescriptionFrequency,
    quantity: String(p.quantity), // FIXME: Need to update this to use the PDE quantity data if isHidden is true
  }));

export const getPharmacyNpis = (pharmacies: Pharmacy[]) =>
  pharmacies.map(p => p.npi);

export const getLisForDrugCosts = ({
  futureLis,
  lis,
  mctCurrentPlanYear,
  plan,
}: {
  futureLis: LowIncomeSubsidyStatus | undefined;
  lis: LowIncomeSubsidyStatus | undefined;
  mctCurrentPlanYear: number;
  plan: Plan | SearchResultPlan;
}): LowIncomeSubsidyStatus => {
  let lisToUse = lis ? lis : LowIncomeSubsidyStatus.LIS_NO_HELP;
  // anon users do not use futureLis
  if (+plan.contract_year !== mctCurrentPlanYear && futureLis) {
    lisToUse = futureLis;
  }
  return lisToUse;
};

export const fetchCostsReturnHandler = (
  res: DrugCostsResponse | undefined,
  defaultDrugCostInfo: DrugCostInfo
) => (res ? res.plans[0] : defaultDrugCostInfo);

/**
 * Optional `fullYearPricing` and `retailOnly` both default to `false`
 */
export const fetchCosts = async ({
  fullYearPricing = false,
  futureLis,
  lis,
  mctCurrentPlanYear,
  prescriptions,
  pharmacies,
  plan,
  retailOnly = false,
  t,
}: {
  fullYearPricing?: boolean;
  futureLis: LowIncomeSubsidyStatus | undefined;
  lis: LowIncomeSubsidyStatus | undefined;
  mctCurrentPlanYear: number;
  prescriptions: PrescriptionDrug[];
  pharmacies: Pharmacy[];
  plan: Plan | SearchResultPlan;
  retailOnly?: boolean;
  t: (id: string) => string;
}): Promise<DrugCostInfo> => {
  const defaultDrugCostInfo = getDefaultDrugCostInfo({ plan, t });
  const prescriptionInfoForDrugCosts =
    getPrescriptionInfoForDrugCosts(prescriptions);
  const npis = getPharmacyNpis(pharmacies);
  const planId = getFullPlanId(plan);

  const lisToUse = getLisForDrugCosts({
    futureLis,
    lis,
    mctCurrentPlanYear,
    plan,
  });

  try {
    const fetchedCostInfo = await getDrugCosts({
      npis,
      prescriptions: prescriptionInfoForDrugCosts,
      planIds: [planId],
      lis: lisToUse,
      fullYearPricing,
      retailOnly,
    });
    return fetchCostsReturnHandler(fetchedCostInfo, defaultDrugCostInfo);
  } catch (err) {
    logError(
      "Could not get drug costs (costsHelpers/fetchCosts)",
      err as ApiError
    );
    return defaultDrugCostInfo;
  }
};

export const useCosts = ({
  fullYearPricing = false,
  pharmacies,
  plan,
  ref,
  retailOnly = false,
}: {
  plan: Plan | SearchResultPlan;
  ref?: RefObject<HTMLElement>;
  pharmacies?: Pharmacy[];
  fullYearPricing?: boolean;
  retailOnly?: boolean;
}): DrugCostInfo => {
  const t = useTranslate();
  const {
    state,
    state: { futureLis, lis, prescriptions },
  } = useContext(AppContext);
  const mctCurrentPlanYear = useCurrentPlanYear();
  const defaultDrugCostInfo = getDefaultDrugCostInfo({ plan, t });
  const [costInfo, setCostInfo] = useState<DrugCostInfo>(defaultDrugCostInfo);
  const pharmaciesToQuery = pharmacies || state.pharmacies;

  const updateCosts = (costs: DrugCostInfo): void => {
    setCostInfo(costs);
    observer.disconnect();
  };

  const fetchCostsAndUpdate = () => {
    fetchCosts({
      fullYearPricing,
      futureLis,
      lis,
      mctCurrentPlanYear,
      pharmacies: pharmaciesToQuery,
      plan,
      prescriptions,
      retailOnly,
      t,
    }).then(drugCostInfo => {
      updateCosts(drugCostInfo);
    });
  };

  const observer = new IntersectionObserver(
    ([entry]: IntersectionObserverEntry[]): void => {
      if (entry.isIntersecting) {
        fetchCostsAndUpdate();
      }
    }
  );

  useEffect(() => {
    if (!mctCurrentPlanYear) {
      return;
    }
    if (isSearchResultPlan(plan) && plan.remaining_premium_and_drugs) {
      return;
    }

    if (ref) {
      if (ref.current) {
        observer.observe(ref.current);
      }
      return (): void => observer.disconnect();
    } else {
      fetchCostsAndUpdate();
      return;
    }
  }, [plan, mctCurrentPlanYear]);

  return costInfo;
};

export const fetchDrugInfo = async ({
  plan,
  prescriptions,
}: {
  plan: IdParts;
  prescriptions: PrescriptionDrug[];
}): Promise<DrugInfo[] | undefined> => {
  const ndcs = prescriptions.map(p => p.ndc);

  if (ndcs.length) {
    const planUrl = makePlanUrlPartForApi(plan);
    try {
      const res = await getOtherDrugInfo(ndcs, planUrl);
      return res;
    } catch (e) {
      logError(
        "Failed to get other drug information (OtherDrugInfo)",
        e as ApiError
      );
      return undefined;
    }
  }

  return undefined;
};
